/**
 * @file This file contains the DisasterManager class, which is responsible for
 * managing random disasters in the game.
 * @module DisasterManager
 */

import { disastersConfig } from '../config/disastersConfig.js';
import { EventEmitter } from '../lib/EventEmitter.js';

export class DisasterManager {
    /**
     * @param {object} dependencies - The dependencies for the DisasterManager class.
     * @param {import('../models/Grid.js').Grid} dependencies.grid - The game grid instance.
     * @param {import('./UIManager.js').UIManager} dependencies.ui - The UI manager instance.
     * @param {import('./AudioManager.js').AudioManager} dependencies.audioManager - The audio manager instance.
     * @param {import('../lib/EventEmitter.js').EventEmitter} dependencies.eventEmitter - The event emitter instance.
     */
    constructor({ grid, ui, gameState, eventEmitter, audioManager }) {
        /** @private */
        this.grid = grid;
        /** @private */
        this.ui = ui;
        /** @private */
        this.gameState = gameState;
        /** @private */
        this.eventEmitter = eventEmitter;
        /** @private */
        this.audioManager = audioManager;
        /** @private */
        this.disasters = disastersConfig;
        /** @private */
        this.activeDisaster = null;
        /** @private */
        this.disasterData = {};
        /** @private @type {Array<string>} */
        this.activeEffectIds = [];
    }

    /**
     * Resets the manager to a clean state, clearing any active disaster.
     */
    reset() {
        this.endDisaster(); // endDisaster handles all the necessary cleanup.
        this.activeDisaster = null;
        this.disasterData = {};
        this.activeEffectIds = [];
    }

    /**
     * Updates the state of any active disasters. This method should be called
     * on each game tick or turn.
     */
    
    update() {
        if (!this.gameState.activeGameConfig.isDisastersEnabled) return;

        if (this.activeDisaster) {
            this._updateActiveDisaster();
        } else {
            this._checkForNewDisaster();
        }
    }

    /** @private */
    _checkForNewDisaster() {
        for (const disasterKey in this.disasters) {
            const disaster = this.disasters[disasterKey];
            if (Math.random() < 1 / disaster.frequency) {
                this.startDisaster(disasterKey);
                break;
            }
        }
    }

    /**
     * Starts a new disaster.
     * @param {string} disasterKey - The key of the disaster to start.
     */
    startDisaster(disasterKey) {
        // Do not start any disaster if the feature is disabled in the settings.
        if (!this.gameState.activeGameConfig.isDisastersEnabled) return;

        if (this.activeDisaster) {
            // If a disaster is somehow still active, end it before starting a new one.
            this.endDisaster();
        }

        const disaster = this.disasters[disasterKey];
        this.activeDisaster = disasterKey;
        this.eventEmitter.emit('game:setIsProcessingTurn', true); // Block user input

        if (disaster.sound) {
            this.audioManager.play(disaster.sound, { type: 'foreground' });
        }

        // Select the display icon for this disaster instance upfront.
        this.disasterData.display = this._getDisplay(disaster);

        switch (disaster.type) {
        case 'slowSweep':
            this._startSlowSweep(disaster);
            break;
        case 'fastSweep':
            this._executeFastSweep(disaster);
            break;
        case 'random':
            this._executeRandomDisaster(disaster);
            break;
        }
    }

    /** @private */
    _updateActiveDisaster() {
        const disaster = this.disasters[this.activeDisaster];
        if (disaster && disaster.type === 'slowSweep') {
            this._updateSlowSweep();
        }
    }

    /** @private */
    _startSlowSweep(disaster) {
        const startEdge = Math.floor(Math.random() * 4);
        let x, y;

        if (startEdge === 0) { // Top
            x = Math.floor(Math.random() * this.grid.playableSize) + this.grid.bufferSize;
            y = this.grid.bufferSize;
        } else if (startEdge === 1) { // Right
            x = this.grid.size - this.grid.bufferSize - 1;
            y = Math.floor(Math.random() * this.grid.playableSize) + this.grid.bufferSize;
        } else if (startEdge === 2) { // Bottom
            x = Math.floor(Math.random() * this.grid.playableSize) + this.grid.bufferSize;
            y = this.grid.size - this.grid.bufferSize - 1;
        } else { // Left
            x = this.grid.bufferSize;
            y = Math.floor(Math.random() * this.grid.playableSize) + this.grid.bufferSize;
        }

        this.disasterData = { x, y, direction: startEdge, steps: 0, display: this.disasterData.display };
        const effectId = this.ui.addTemporaryEffect(y, x, { display: this.disasterData.display, backgroundColor: disaster.backgroundColor });
        // For slow sweep, we only track the single, persistent effect.
        this.activeEffectIds = [effectId];
        if (this.grid.get(y, x) && !this.grid.get(y, x).isStationary) {
            this.eventEmitter.emit('game:gemDestroyed');
        }
        this.grid.set(y, x, null);
        this.ui.updateGridCell(this.ui.dom.grid.children[y * this.grid.size + x], null, this.gameState.activeGameConfig);
        this.eventEmitter.emit('game:setIsProcessingTurn', false); // Allow next turn
    }

    /** @private */
    _updateSlowSweep() {
        let { x, y, direction, steps, display } = this.disasterData;
        const disaster = this.disasters[this.activeDisaster];

        // Clear the previous effect's DOM element.
        if (this.activeEffectIds.length > 0) {
            this.ui.clearTemporaryEffectById(this.activeEffectIds[0]);
        }

        // Calculate the next potential position
        const randomOffset = Math.floor(Math.random() * 3) - 1;
        let nextX = x;
        let nextY = y;

        // Primary direction is VERTICAL (starts from top or bottom)
        if (direction === 0 || direction === 2) { 
            nextY = (direction === 0) ? y + 1 : y - 1; // Move down or up
            nextX = x + randomOffset; // Wander left or right
        } 
        // Primary direction is HORIZONTAL (starts from left or right)
        else { 
            nextX = (direction === 1) ? x - 1 : x + 1; // Move left or right
            nextY = y + randomOffset; // Wander up or down
        }
      
        steps++;

        // If the next position is not in the playable area, end the disaster.
        if (!this.grid.isPlayable(nextY, nextX)) {
            this.endDisaster();
            return;
        }

        // If the move is valid, apply the effect and update the state.
        const newEffectId = this.ui.addTemporaryEffect(nextY, nextX, { display, backgroundColor: disaster.backgroundColor });
        this.activeEffectIds = [newEffectId]; // Replace with the new effect ID.
        if (this.grid.get(nextY, nextX) && !this.grid.get(nextY, nextX).isStationary) {
            this.eventEmitter.emit('game:gemDestroyed');
        }
        this.grid.set(nextY, nextX, null);
        this.ui.updateGridCell(this.ui.dom.grid.children[nextY * this.grid.size + nextX], null, this.gameState.activeGameConfig);
        this.disasterData = { x: nextX, y: nextY, direction, steps, display };
    }

    /** @private */
    _executeFastSweep(disaster) {
        const isHorizontal = Math.random() < 0.5;
        // The starting position on the secondary axis.
        let secondaryAxisPos = Math.floor(Math.random() * this.grid.playableSize) + this.grid.bufferSize;

        // The loop iterates along the primary axis, ensuring it always moves forward.
        for (let i = 0; i < this.grid.playableSize; i++) {
            // The secondary axis can deviate randomly.
            const offset = Math.floor(Math.random() * 3) - 1; // -1, 0, or 1
            secondaryAxisPos += offset;

            const x = isHorizontal ? this.grid.bufferSize + i : secondaryAxisPos;
            const y = isHorizontal ? secondaryAxisPos : this.grid.bufferSize + i;

            if (this.grid.isPlayable(y, x)) {
                const effectId = this.ui.addTemporaryEffect(y, x, { display: this._getDisplay(disaster), duration: disaster.effect.duration, backgroundColor: disaster.backgroundColor });
                this.activeEffectIds.push(effectId);
                if (this.grid.get(y, x) && !this.grid.get(y, x).isStationary) {
                    this.eventEmitter.emit('game:gemDestroyed');
                }
                this.grid.set(y, x, null);
            } else {
                // If the path hits a non-playable cell (like the buffer zone), end the sweep.
                break;
            }
        }
        this.ui.updateGrid(this.grid.data, this.grid.size, this.gameState.activeGameConfig);
        // Don't call endDisaster immediately. The UIManager's lifecycle will handle the fade-out.
        // We just need to clear the active disaster state after the duration.
        const duration = disaster.effect.duration || 1000;
        setTimeout(() => this.endDisaster(), duration);
    }

    /** @private */
    _executeRandomDisaster(disaster) {
        const numToRemove = Math.floor(this.grid.playableSize / 2);
        const cells = this.grid.getRandomPlayableCells(numToRemove);

        cells.forEach(cell => {
            const effectId = this.ui.addTemporaryEffect(cell.r, cell.c, { display: this._getDisplay(disaster), duration: disaster.effect.duration, backgroundColor: disaster.backgroundColor });
            this.activeEffectIds.push(effectId);
            if (this.grid.get(cell.r, cell.c) && !this.grid.get(cell.r, cell.c).isStationary) {
                this.eventEmitter.emit('game:gemDestroyed');
            }
            this.grid.set(cell.r, cell.c, null);
        });
        this.ui.updateGrid(this.grid.data, this.grid.size, this.gameState.activeGameConfig);
        // Don't call endDisaster immediately. Let the effects fade out naturally.
        const duration = disaster.effect.duration || 1000;
        setTimeout(() => this.endDisaster(), duration);
    }
    
    /**
     * Gets a random display icon from a disaster's display configuration.
     * @param {object} disaster - The disaster configuration object.
     * @returns {string} A single display string (e.g., an emoji).
     * @private
     */
    _getDisplay(disaster) {
        const display = disaster.display;
        if (Array.isArray(display)) {
            return display[Math.floor(Math.random() * display.length)];
        }
        return display;
    }

    /**
     * Ends the current disaster and cleans up.
     */
    endDisaster() {
        // Manually clear any persistent effects like the slow-sweep icon.
        // Timed effects from other disasters will fade on their own.
        this.activeEffectIds.forEach(id => this.ui.clearTemporaryEffectById(id));

        if (this.activeDisaster) {
            this.eventEmitter.emit('achievement:disasterSurvived', { gameState: this.gameState });
        }
        this.clearActiveDisasterState();
    }

    /**
     * Clears the internal state of the active disaster without immediately removing visual effects.
     * This allows effects to fade out naturally.
     * @private
     */
    clearActiveDisasterState() {
        // Clear the internal tracking of effect IDs, but don't tell the UI to remove them.
        // The UIManager's lifecycle will handle their removal after their duration expires.
        this.activeEffectIds = [];
        this.activeDisaster = null;
        this.disasterData = {};
        this.eventEmitter.emit('game:setIsProcessingTurn', false); // Allow player input again
    }

    /**
     * Gets the current state of the disaster manager for saving.
     * @returns {object} The current disaster state.
     */
    getState() {
        return {
            activeDisaster: this.activeDisaster,
            disasterData: this.disasterData,
        };
    }

    /**
     * Restores the state of the disaster manager from a saved state.
     * @param {object} state - The saved disaster state.
     */
    restoreState(state) {
        this.activeDisaster = state.activeDisaster;
        this.disasterData = state.disasterData || {};

        // If restoring a slowSweep disaster, we need to recreate its visual effect on the grid.
        if (this.activeDisaster && this.disasters[this.activeDisaster]?.type === 'slowSweep' && this.disasterData.x !== undefined) {
            const disaster = this.disasters[this.activeDisaster];
            const { x, y, display } = this.disasterData;
            const effectId = this.ui.addTemporaryEffect(y, x, { display, backgroundColor: disaster.backgroundColor });
            this.activeEffectIds = [effectId];
        }
        this.update();
    }
}